import { Suspense, useMemo } from 'react'

import BusinessMap from 'src/features/business/components/business-map'
import BusinessTeamMemberAvatars from 'src/features/business/components/business-team-member-avatars'
import type { TeamUserSummary } from 'src/features/business/shared/schemas/business'
import { SharedBusinessCardSkeleton } from 'src/features/business/skeletons/shared-business-card-skeleton'
import type { DesiredLocation, ExactLocation } from 'src/features/consumers/schemas/consumer'
import { CoverPhotoFallback } from 'src/shared/features/users/components/profile-photos/cover-photo'
import { cn } from 'src/shared/utils/cn'

type SharedBusinessCardProps = {
  name: string
  mapSnapshot?: string | null
  onClick?: () => void
  className?: string
  desiredLocations?: DesiredLocation[]
  exactLocations?: ExactLocation[]
  teamMembers?: TeamUserSummary[]
}

const SharedBusinessCardContent = ({
  name,
  mapSnapshot,
  desiredLocations,
  exactLocations,
  onClick,
  className,
  teamMembers,
}: SharedBusinessCardProps) => {
  const hasDesiredLocations = desiredLocations && desiredLocations.length > 0
  const hasExactLocations = exactLocations && exactLocations.length > 0

  const latLng = useMemo(() => {
    if (hasDesiredLocations) {
      const loc = desiredLocations?.[0]
      return {
        lat: loc?.latitude,
        lng: loc?.longitude,
      }
    }
    if (hasExactLocations) {
      const loc = exactLocations?.[0]
      return {
        lat: loc?.latitude,
        lng: loc?.longitude,
      }
    }
    return null
  }, [hasDesiredLocations, hasExactLocations, desiredLocations, exactLocations])

  const renderMapSnapshot = () => {
    if (mapSnapshot) {
      return <img src={mapSnapshot} alt="" className="size-full rounded-[15px] object-cover sm:rounded-none" />
    }
    if (latLng) {
      return (
        <BusinessMap
          latitude={latLng.lat}
          longitude={latLng.lng}
          markers={[{ lat: latLng.lat, lng: latLng.lng }]}
          className="size-full rounded-[15px] object-cover sm:rounded-none"
        />
      )
    }
    return <img src={CoverPhotoFallback} alt="" className="size-full rounded-[15px] object-cover sm:rounded-none" />
  }

  return (
    <div
      className={cn(
        'group relative flex w-full flex-col gap-2 xs:max-w-[280px]',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
    >
      <div className="relative h-[150px] w-full max-w-[280px] overflow-hidden rounded-[20px] sm:h-[180px]">
        {renderMapSnapshot()}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(0deg, rgba(27, 48, 41, 0.15) 0%, rgba(27, 48, 41, 0.00) 100%)',
          }}
        />
        <div className="absolute bottom-2.5 left-2.5">
          <BusinessTeamMemberAvatars teamMembers={teamMembers} size={40} />
        </div>
      </div>
      <div className="flex flex-col gap-0 sm:w-auto">
        <div className="line-clamp-1 text-[15px] font-bold xs:text-[18px]">{name}</div>
        <div className="line-clamp-1 align-middle text-[13px] tracking-[-0.28px] text-muted xs:text-[14px]">
          {/* {badges?.map((badge, index) => (
              <span key={index}>
                {badge} {index < badges.length - 1 && <InlineDot />}
              </span>
            ))} */}
        </div>
        <div className="line-clamp-1 flex flex-row items-center gap-2 align-middle text-[13px] tracking-[-0.28px] text-muted xs:text-[14px]">
          Buy &bull; Sell &bull; Rent &bull; Lease
        </div>
      </div>
    </div>
  )
}

const SharedBusinessCard = ({
  name,
  mapSnapshot,
  desiredLocations,
  exactLocations,
  onClick,
  className,
  teamMembers,
}: SharedBusinessCardProps) => {
  return (
    <Suspense fallback={<SharedBusinessCardSkeleton />}>
      <SharedBusinessCardContent
        name={name}
        mapSnapshot={mapSnapshot}
        desiredLocations={desiredLocations}
        exactLocations={exactLocations}
        onClick={onClick}
        className={className}
        teamMembers={teamMembers}
      />
    </Suspense>
  )
}

export default SharedBusinessCard
